// lib
import React, { useContext, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import ADND from '@aytm/lib/adnd/ADND'
import { DataContext } from '@aytm/app/stats/page/question-views/components/mapping/Component'

import { generateUID, encodeText, decodeText } from '@aytm/app/stats/lib/helpers'

// components
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'

// hooks
import useSingleAndDoubleClick from '@aytm/app/stats/hooks/useSingleAndDoubleClick'

// styles
import style from '@aytm/app/stats/page/question-views/components/mapping/styles/Component.module.scss'

const Entity = ({ item, position, imagesDisabled, dndItemsList }) => {
  const { id, mode, data, disabled, dispatch } = useContext(DataContext)

  const dndListItemRef = useRef()
  const dndListItemHandleRef = useRef()

  const fetchData = useCallback(() => {
    return {
      id,
      item,
      position: position - 1,
      windowUID: data.windowUID,
      remove: () =>
        dispatch({
          type: 'DELETE_ENTITY',
          data: position - 1
        })
    }
  }, [item, position, id, data.windowUID, dispatch])

  const dndListItem = ADND.targets.itemsList.listItem({
    type: `dlq-entity-${data.windowUID}`,
    list: dndItemsList
  })
  const dndListItemHandle = ADND.sources
    .listItemHandle({
      type: `dlq-entity-${data.windowUID}`,
      item: dndListItem,
      cloneable: true
    })
    .fetchData(fetchData)

  const itemImageTemplate = option =>
    option ? (
      <>
        {option.src && <img width="23" height="23" src={option.src} alt="" />}
        <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
      </>
    ) : (
      <i className="icon-aytmfd-image" />
    )
  const valueImageTemplate = option =>
    option && option.src ? <img width="23" height="23" src={option.src} alt="" /> : <i className="icon-aytmfd-image" />

  const onLabelChange = event => {
    dispatch({
      type: 'UPDATE_ENTITY',
      data: {
        item: {
          ...item,
          text: encodeText(event.target.value)
        },
        position: position - 1
      }
    })
  }

  const onImageChange = event => {
    if ('off' == event.value) delete item.image
    else item.image = event.value

    dispatch({
      type: 'UPDATE_ENTITY',
      data: {
        item: {
          ...item
        },
        position: position - 1
      }
    })
  }

  const onDeleteClick = () => {
    dispatch({
      type: 'DELETE_ENTITY',
      data: position - 1
    })
  }

  const onHandleClick = useSingleAndDoubleClick(null, () => {
    dispatch({
      type: 'ADD_ENTITY',
      data: {
        item: {
          ...item,
          pattern: '',
          key: generateUID()
        },
        position: position
      }
    })
  })

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  useEffect(() => {
    if (dndListItemHandle) dndListItemHandle.connect(dndListItemHandleRef.current)

    return () => {
      if (dndListItemHandle) dndListItemHandle.disconnect()
    }
  }, [dndListItemHandle])

  const renderLabel = () =>
    imagesDisabled ? (
      <InputText
        placeholder="Please type a label..."
        value={decodeText(item.text)}
        maxLength={120}
        disabled={disabled}
        onChange={disabled ? null : onLabelChange}
      />
    ) : (
      <>
        <div className={`${style.images} p-field p-col-12`}>
          <InputText
            placeholder="Please type a label..."
            value={decodeText(item.text)}
            maxLength={120}
            disabled={disabled}
            onChange={disabled ? null : onLabelChange}
          />
        </div>
        <Dropdown
          className="p-dropdown-icon p-dropdown-right"
          placeholder=""
          value={item.image || ''}
          options={data.images}
          optionValue="key"
          optionLabel="text"
          itemTemplate={itemImageTemplate}
          valueTemplate={valueImageTemplate}
          fixedDropdownDir="bottom"
          disabled={disabled}
          onChange={disabled ? null : onImageChange}
        />
      </>
    )

  const renderAdvanced = () => <div className={`${style.group} p-fluid p-field p-col-12`}>{renderLabel()}</div>
  const renderBasic = () => (
    <>
      <div className="p-fluid p-field p-col-4">
        <InputText value={item.pattern} disabled={true} />
      </div>
      <div className={`${style.group} p-fluid p-field p-col`}>{renderLabel()}</div>
    </>
  )

  return (
    <div ref={dndListItemRef} className={`PRAYTMTheme ${style.row} ${style['row-av']} p-fluid p-col-12`}>
      {disabled ? null : (
        <span className={style.delete} onClick={onDeleteClick}>
          <i className="icon-aytmfd-delete" />
        </span>
      )}
      <div className={`${style.index} p-field`}>{position}.</div>
      <div
        className={`${style.group} ${style['group-av']}${
          disabled ? ' ' + style['group-av-disabled'] : ''
        } p-fluid p-field p-col`}
      >
        {disabled || 'advanced' == mode ? renderAdvanced() : renderBasic()}
      </div>
      {disabled ? null : (
        <div className="p-field">
          <span ref={dndListItemHandleRef} className={style.handle} onClick={onHandleClick}>
            <i className="icon-aytmfd-handle" />
          </span>
        </div>
      )}
    </div>
  )
}

Entity.propTypes = {
  item: PropTypes.object,
  position: PropTypes.number,
  imagesDisabled: PropTypes.bool,
  dndItemsList: PropTypes.any
}

Entity.displayName = 'Entity'

export default Entity
